import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const useBooks = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all books
  const fetchBooks = async () => {
    try {
      setLoading(true);
      const booksRef = collection(db, 'books');
      const q = query(booksRef, orderBy('title', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const booksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure author field exists (default to null if not present)
        author: doc.data().author || null,
        // Ensure all required fields have defaults
        title: doc.data().title || 'Untitled Book',
        description: doc.data().description || 'No description available',
        amount: doc.data().amount || '0',
        image_url: doc.data().image_url || null,
        pdf_url: doc.data().pdf_url || null,
        updated_at: doc.data().updated_at || null
      }));
      
      setBooks(booksData);
      setError(null);
    } catch (err) {
      console.error('Error fetching books:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search books by title, description, or author
  const searchBooks = (searchTerm) => {
    if (!searchTerm.trim()) return books;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return books.filter(book => 
      book.title?.toLowerCase().includes(lowerSearchTerm) ||
      book.description?.toLowerCase().includes(lowerSearchTerm) ||
      book.author?.toLowerCase().includes(lowerSearchTerm)
    );
  };

  // Get book by ID
  const getBookById = async (bookId) => {
    try {
      const bookDoc = await getDoc(doc(db, 'books', bookId));
      if (bookDoc.exists()) {
        return {
          id: bookDoc.id,
          ...bookDoc.data(),
          // Ensure author field exists
          author: bookDoc.data().author || null
        };
      }
      return null;
    } catch (err) {
      console.error('Error fetching book:', err);
      return null;
    }
  };

  // Get books by category (if category field exists)
  const getBooksByCategory = (category) => {
    if (!category) return books;
    return books.filter(book => book.category === category);
  };

  // Get books by price range
  const getBooksByPriceRange = (minPrice, maxPrice) => {
    return books.filter(book => {
      const price = parseFloat(book.amount) || 0;
      return price >= minPrice && price <= maxPrice;
    });
  };

  // Get free books
  const getFreeBooks = () => {
    return books.filter(book => {
      const price = parseFloat(book.amount) || 0;
      return price === 0;
    });
  };

  // Get paid books
  const getPaidBooks = () => {
    return books.filter(book => {
      const price = parseFloat(book.amount) || 0;
      return price > 0;
    });
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  return {
    books,
    loading,
    error,
    fetchBooks,
    searchBooks,
    getBookById,
    getBooksByCategory,
    getBooksByPriceRange,
    getFreeBooks,
    getPaidBooks
  };
};

export default useBooks;
